import { Component, Input } from '@angular/core';

import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { indexOf } from 'lodash';

import { AttributeModel } from '../../../../Common/Misc/models/attribute.model';
import { attributeTypesEnum } from '../../../../Common/Misc/enums/attribute-types.enum';
import { mediaSubscriptionAttributeTypes } from '@Monitor/MediaSubscriptions/enums/media-subscription-attribute-types.enum';
import { MediaSubscription } from '@Monitor/Shared/models/media-subscription.model';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { ThemeService } from '@Social/Intelligence/services/theme.service';
import { MediaSubscriptionMediaTypeEnum } from '../../../../api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { NetworkTypeEnum, networkOptionsOrder as networkOrder  } from '../../../Core/enums/network-type.enum';
import { GeoRadiusUnit } from '@Common/Misc/models/geographical-circle.model';
import { JournodexRegionsService } from '@Common/Journodex/services/journodex-regions.service';
import { IncludeExcludeModel } from '@Common/Misc/models/include-exclude-model';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';

export type TwitterAdditionalSettings = Pick<MediaSubscription, 'samplePercentage' | 'countryCodes' | 'verifiedAuthor' | 'languageFilter' | 'geoCircles'>;
@Component({
    selector: 'agpr-agent-summary',
    templateUrl: './agent-summary.component.html',
    styleUrls: ['./agent-summary.component.scss'],
    standalone: false
})
export class AgentSummaryComponent {

    @Input() set agent (agent: MediaSubscription) {
        this.availableNetworks = [];
        this._agent = agent;
        this.setAttributes();
        if (this._agent) {
            this.themeService.all$.pipe(take(1)).subscribe(themes => {
                this.themeNameList = '';
                this._agent.themeIds.forEach((id, index) => {
                    const matchedTheme = themes.find(x => x.id === id);
                    if (matchedTheme) {
                        if (index !== 0) {
                            this.themeNameList += `, ${matchedTheme.name}`;
                        } else {
                            this.themeNameList += matchedTheme.name;
                        }
                    }
                });
            });

            // localization token for languages
            const twitterLanguageFilter = new IncludeExcludeModel<string>({
                include: this._agent.languageFilter?.include?.map(x => 'Language.' + x) ?? [],
                exclude: this._agent.languageFilter?.exclude?.map(x => 'Language.' + x) ?? []
            });
            this.twitterAdditionalSettings = {
                samplePercentage: this._agent.samplePercentage,
                countryCodes: this._agent.countryCodes?.map(code => this.journodexRegionsService.getCountryByIsoCode(code).name) ?? [],
                geoCircles: this._agent.geoCircles,
                verifiedAuthor: this._agent.verifiedAuthor,
                languageFilter: twitterLanguageFilter
            };

            this._agent.mediaTypeRequests?.forEach(mediaTypeRequest => {
                let socialMediaType = mediaTypeRequest.mediaType;
                if (socialMediaType === MediaSubscriptionMediaTypeEnum.subReddit) {
                    // subreddit belongs to Reddit
                    socialMediaType = MediaTypeEnum.REDDIT;
                }
                if (!this.availableNetworks.some(x => x === socialMediaType)) {
                    // TV and RADIO aren't supported in social, therefore save to convert
                    this.availableNetworks.push(socialMediaType as MediaSubscriptionMediaTypeEnum);
                }
            });
        }

    };

    @Input() set networks (value: NetworkTypeEnum[]) {
        if (value) {
            this._networks = value;
            this._networks.sort((a, b) => {
                return indexOf(networkOrder, a) - indexOf(networkOrder, b);
            });
        }
    }

    @Input() showDetails: boolean = true;

    _agent: MediaSubscription;
    availableNetworks: MediaSubscriptionMediaTypeEnum[] = [];
    _networks: NetworkTypeEnum[] = [];

    attributes: AttributeModel[] = [];
    subscription: Subscription[] = [];
    themeNameList: string = '';
    twitterAdditionalSettings: TwitterAdditionalSettings;

    readonly networkTypes = NetworkTypeEnum;
    readonly geoRadiusUnit = GeoRadiusUnit;
    readonly mediaSubscriptionMediaTypes = MediaSubscriptionMediaTypeEnum;

    constructor(
        private localizationService: LocalizationService,
        private themeService: ThemeService,
        public journodexRegionsService: JournodexRegionsService,
    ) {
    }


    private setAttributes() {

        if (!this._agent) {
            this.attributes = [];
            return;
        }

        const createdByAttribute: AttributeModel = {
            id: mediaSubscriptionAttributeTypes.createdBy,
            label: this.localizationService.transform('createdBy'),
            value: this._agent.createdByUserId,
            type: attributeTypesEnum.user
        };

        const createdDateAttribute: AttributeModel = {
            id: mediaSubscriptionAttributeTypes.createdDate,
            label: this.localizationService.transform('dateCreated'),
            value: this._agent.createdDate,
            type: attributeTypesEnum.date
        };

        const modifiedByAttribute: AttributeModel = {
            id: mediaSubscriptionAttributeTypes.modifiedBy,
            label: this.localizationService.transform('lastModifiedBy'),
            value: this._agent.modifiedByUserId,
            type: attributeTypesEnum.user
        };

        const modifiedDateAttribute: AttributeModel = {
            id: mediaSubscriptionAttributeTypes.modifiedDate,
            label: this.localizationService.transform('lastModifiedDate'),
            value: this._agent.modifiedDate,
            type: attributeTypesEnum.date
        };

        this.attributes = [
            createdByAttribute,
            createdDateAttribute,
        ];

        if (this._agent.modifiedByUserId && this._agent.modifiedDate) {
            this.attributes.push(modifiedByAttribute, modifiedDateAttribute);
        }
    }
}
